import { useQuery } from '@tanstack/react-query';
import { useAppSelector } from '@/store/hooks';
import { useUnifiedAuth } from '@/hooks/useUnifiedAuth';
import {
  fetchEnvironmentalData,
  type EnvironmentalMaterialTotal,
  type EnvironmentalCo2Totals,
  type EnvironmentalCo2ByMonth,
  type EnvironmentalUnitRankingRow,
} from '@/integrations/agreements/client';
import { filterCutoffMonths, resolveEffectiveGeneratorIds } from '@/config/legacyResicleanCutoff';

export type { EnvironmentalMaterialTotal, EnvironmentalCo2Totals, EnvironmentalCo2ByMonth };

/** Linha do ranking com o nome da unidade, composto no front a partir do /generators/me (store). */
export interface EnvironmentalUnitRanking extends Omit<EnvironmentalUnitRankingRow, 'establishment'> {
  establishment: { id: number; name: string };
}

export interface IAFilterState {
  months: string[];
  establishments: string[];
  generators: string[];
}

export function useEnvironmentalData(filters: IAFilterState) {
  const { profile } = useUnifiedAuth();
  const establishmentsFromRedux = useAppSelector((s) => s.establishmentAccess.establishments);
  const generatorsFromRedux = useAppSelector((s) => s.establishmentAccess.generators);

  const establishment_id: number[] | null =
    filters.establishments.length > 0
      ? filters.establishments
          .map((externalId) => {
            const found = establishmentsFromRedux.find(
              (e) => e.external_id === externalId || e.id === externalId,
            );
            return found ? parseInt(found.id, 10) : null;
          })
          .filter((id): id is number => id !== null)
      : null;

  const generator_id: number[] | null =
    filters.generators.length > 0
      ? filters.generators
          .map((externalId) => {
            const found = generatorsFromRedux.find(
              (g) => g.external_id === externalId || g.id === externalId,
            );
            return found ? parseInt(found.id, 10) : null;
          })
          .filter((id): id is number => id !== null)
      : null;

  const sortedMonths = [...filters.months].sort();

  // MC-107: remove os meses com dados retroativos fictícios (jan-jul/2026)
  // quando a consulta é inteiramente escopada a gerador(es) da carteira
  // Resiclean onboardados sem esses dados. Ver src/config/legacyResicleanCutoff.ts.
  const effectiveGeneratorIds = resolveEffectiveGeneratorIds(generator_id, profile?.generator_id ?? null);
  const visibleMonths = filterCutoffMonths(sortedMonths, effectiveGeneratorIds);

  const { data, isLoading, error } = useQuery({
    queryKey: ['environmental-data', visibleMonths.join(','), establishment_id, generator_id],
    queryFn: () => fetchEnvironmentalData({ months: visibleMonths, establishment_id, generator_id }),
    staleTime: 5 * 60 * 1000,
  });

  // O agreements não conhece nomes de unidade: vêm do /generators/me, que já está no store. Sem
  // cadastro para o id (unidade inativa, fora do escopo), fica o próprio id — a linha não some.
  const unitRanking: EnvironmentalUnitRanking[] = (data?.unit_ranking ?? []).map((row) => ({
    ...row,
    establishment: {
      id: row.establishment.id,
      name: establishmentsFromRedux.find((e) => e.id === String(row.establishment.id))?.name ?? String(row.establishment.id),
    },
  }));

  return {
    materialTotals: data?.material_totals ?? [],
    co2Totals: data?.co2_totals ?? null,
    co2Evolution: data?.co2_evolution ?? [],
    unitRanking,
    isLoading,
    error,
  };
}
